import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, MapPin, Clock } from "lucide-react";
import { BUSINESS } from "@/data/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", brand: "", product: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18400862307/0QQ-CKjK9OQcEOPAm8ZE",
          value: 1.0,
          currency: "INR",
        });
      }
      toast.success("Request received. We'll call you back shortly.");
      setForm({ name: "", phone: "", brand: "", product: "", message: "" });
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-transparent border-b border-[#222222] focus:border-[#FF3B30] outline-none text-white px-0 py-4 text-base transition-colors w-full font-mono-plex placeholder-[#555555]";

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-[#222222]">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-24 md:py-40 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Left — info */}
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-mono-plex font-bold">
            [ 05 — Get in touch ]
          </span>
          <h2 className="font-display font-bold uppercase tracking-tight text-white text-5xl md:text-7xl mt-6 leading-[0.85]">
            Book a<br />repair today
          </h2>

          <address className="not-italic mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 border border-[#222222] flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 text-[#FF3B30]" />
              </span>
              <span className="flex flex-col gap-1">
                {BUSINESS.phones.map((p, i) => (
                  <a
                    key={p.raw}
                    data-testid={`contact-phone-${i}`}
                    href={`tel:${p.raw}`}
                    className="font-mono-plex text-white hover:text-[#FF3B30] transition-colors"
                  >
                    {p.display}
                  </a>
                ))}
              </span>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 border border-[#222222] flex items-center justify-center shrink-0">
                <MessageCircle className="w-4 h-4 text-[#FF3B30]" />
              </span>
              <span className="flex flex-col gap-1">
                {BUSINESS.phones.map((p, i) => (
                  <a
                    key={p.raw}
                    data-testid={`contact-whatsapp-${i}`}
                    href={`https://wa.me/${p.raw.replace("+", "")}?text=${encodeURIComponent(BUSINESS.whatsappText)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono-plex text-white hover:text-[#FF3B30] transition-colors"
                  >
                    WhatsApp {p.display}
                  </a>
                ))}
              </span>
            </div>
            <a data-testid="contact-email" href={`mailto:${BUSINESS.email}`} className="flex items-center gap-4 group">
              <span className="w-10 h-10 border border-[#222222] flex items-center justify-center group-hover:border-[#FF3B30] transition-colors">
                <Mail className="w-4 h-4 text-[#FF3B30]" />
              </span>
              <span className="font-mono-plex text-white group-hover:text-[#FF3B30] transition-colors break-all">{BUSINESS.email}</span>
            </a>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 border border-[#222222] flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-[#FF3B30]" />
              </span>
              <span className="font-mono-plex text-[#A0A0A0] leading-relaxed">
                <span className="block text-white">{BUSINESS.addressName}</span>
                {BUSINESS.addressLines.map((l) => (
                  <span key={l} className="block">{l}</span>
                ))}
                <a
                  data-testid="contact-map-link"
                  href={BUSINESS.mapLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 mt-2 text-[#FF3B30] hover:text-white transition-colors uppercase tracking-[0.15em] text-xs"
                >
                  View on Google Maps →
                </a>
              </span>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 border border-[#222222] flex items-center justify-center shrink-0">
                <Clock className="w-4 h-4 text-[#FF3B30]" />
              </span>
              <span className="font-mono-plex text-[#A0A0A0]">
                {BUSINESS.hours.map((h) => (
                  <span key={h.day} className="block">
                    <span className="text-white">{h.day}</span> — {h.time}
                  </span>
                ))}
              </span>
            </div>
          </address>

          <a
            href={BUSINESS.mapLink}
            target="_blank"
            rel="noreferrer"
            data-testid="map-embed-link"
            className="block mt-10 border border-[#222222] hover:border-[#FF3B30] transition-colors group relative"
          >
            <iframe
              title="Manasa Customer Care Service location map"
              src={BUSINESS.mapEmbed}
              className="w-full h-[220px] md:h-[260px] grayscale contrast-125 opacity-80 group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-500 pointer-events-none"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <span className="absolute bottom-0 left-0 bg-[#FF3B30] text-[#050505] px-4 py-2 uppercase tracking-[0.15em] text-[10px] font-bold font-mono-plex">
              Open in Google Maps →
            </span>
          </a>
        </div>

        {/* Right — form */}
        {submitted ? (
          <motion.div
            data-testid="quote-thankyou"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="lg:pt-16 flex flex-col gap-6 border border-[#FF3B30] p-8 md:p-12 self-start"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-mono-plex font-bold">
              [ ✦ Received ]
            </span>
            <h3 className="font-display font-bold text-white text-3xl md:text-4xl leading-[0.95]">
              Thank you.<br />We'll call you back shortly.
            </h3>
            <p className="text-[#A0A0A0] text-sm font-mono-plex leading-relaxed">
              Your request has been received. Our team will reach out on the number you provided within business hours.
            </p>
            <button
              data-testid="thankyou-send-another"
              onClick={() => setSubmitted(false)}
              className="self-start mt-2 border border-[#222222] text-white px-6 py-3 uppercase tracking-[0.15em] text-[10px] font-bold hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors font-mono-plex"
            >
              Send another request
            </button>
          </motion.div>
        ) : (
        <motion.form
          data-testid="quote-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col gap-8 lg:pt-16"
        >
          <input data-testid="input-name" className={inputCls} placeholder="Your name *" value={form.name} onChange={handle("name")} />
          <input data-testid="input-phone" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={handle("phone")} />
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-mono-plex font-bold block mb-6">
              [ Brand & Product ]
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <input data-testid="input-brand" className={inputCls} placeholder="Brand (e.g. Samsung)" value={form.brand} onChange={handle("brand")} />
              <input data-testid="input-product" className={inputCls} placeholder="Product (e.g. AC, Fridge)" value={form.product} onChange={handle("product")} />
            </div>
          </div>
          <textarea data-testid="input-message" rows={3} className={inputCls + " resize-none"} placeholder="Describe the problem" value={form.message} onChange={handle("message")} />
          <button
            data-testid="submit-quote"
            type="submit"
            disabled={loading}
            className="bg-[#FF3B30] text-white px-8 py-5 uppercase tracking-[0.2em] text-xs font-bold transition-all hover:bg-white hover:text-black font-mono-plex disabled:opacity-50 mt-4 self-start"
          >
            {loading ? "Sending…" : "Request a Quote →"}
          </button>
        </motion.form>
        )}
      </div>
    </section>
  );
};

export default Contact;
